import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { 
  HomeIcon, 
  TicketIcon, 
  InformationCircleIcon, 
  UserIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline'
import {
  HomeIcon as HomeIconSolid,
  TicketIcon as TicketIconSolid,
  InformationCircleIcon as InformationCircleIconSolid,
  UserIcon as UserIconSolid,
  ClipboardDocumentCheckIcon as ClipboardDocumentCheckIconSolid
} from '@heroicons/react/24/solid'

const BottomNavBar = () => {
  const location = useLocation()
  const { user } = useAuth()
  const [activeEventId, setActiveEventId] = useState(null)

  // Scan localStorage for any active judging session on mount and route changes
  useEffect(() => {
    const getActiveJudgeEventId = () => {
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && key.startsWith('judgeId_')) {
            const eventId = key.replace('judgeId_', '')
            if (eventId) return eventId
          }
        }
      } catch (e) {
        console.error('Error reading localStorage for active session:', e)
      }
      return null
    }

    setActiveEventId(getActiveJudgeEventId())
  }, [location])

  const pathname = location.pathname

  // Determine active tab
  const isHomeActive = pathname === '/'
  const isAboutActive = pathname === '/about'
  const isJudgeActive = pathname.startsWith('/judge/') || pathname === '/access-event'
  const isAdminActive = pathname === '/admin' || pathname === '/auth' || pathname.startsWith('/admin/') || pathname === '/create-event' || pathname === '/import-template'

  // Navigation config
  const navItems = [
    {
      label: 'Home',
      path: '/',
      isActive: isHomeActive,
      iconOutline: HomeIcon,
      iconSolid: HomeIconSolid,
    },
    {
      label: activeEventId ? 'Scoring' : 'Join Event',
      path: activeEventId ? `/judge/${activeEventId}` : '/access-event',
      isActive: isJudgeActive,
      iconOutline: activeEventId ? ClipboardDocumentCheckIcon : TicketIcon,
      iconSolid: activeEventId ? ClipboardDocumentCheckIconSolid : TicketIconSolid,
    },
    {
      label: 'About',
      path: '/about',
      isActive: isAboutActive,
      iconOutline: InformationCircleIcon,
      iconSolid: InformationCircleIconSolid,
    },
    {
      label: user ? 'Dashboard' : 'Admin',
      path: user ? '/admin' : '/auth',
      isActive: isAdminActive,
      iconOutline: UserIcon,
      iconSolid: UserIconSolid,
    }
  ]

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-t border-gray-200/60 dark:border-slate-800/60 shadow-lg px-2 pb-safe">
      <div className="flex justify-around items-center h-16 max-w-md mx-auto">
        {navItems.map((item) => {
          const Icon = item.isActive ? item.iconSolid : item.iconOutline
          return (
            <Link
              key={item.label}
              to={item.path}
              className="flex flex-col items-center justify-center w-20 h-full text-center relative group transition-all duration-200"
            >
              <div 
                className={`p-1 rounded-xl transition-all duration-300 ${
                  item.isActive 
                    ? 'text-green-500 scale-110' 
                    : 'text-gray-500 hover:text-green-500 hover:scale-105'
                }`}
              >
                <Icon className="w-6 h-6" />
              </div>
              <span 
                className={`text-[10px] font-semibold tracking-wide transition-colors duration-200 mt-0.5 ${
                  item.isActive 
                    ? 'text-green-500 font-bold' 
                    : 'text-gray-500 group-hover:text-green-500'
                }`}
              >
                {item.label}
              </span>
              
              {/* Active Tab Dot Indicator */}
              {item.isActive && (
                <span className="absolute bottom-1.5 w-1 h-1 bg-green-500 rounded-full animate-pulse" />
              )}
            </Link>
          )
        })}
      </div>
    </div>
  )
}

export default BottomNavBar
